import { createContext, useContext } from 'react';

/**
 * Shared Studio video state (cue-forge-9yc.9). Lives above the routes so a
 * reload can offer to re-open the video from a persisted File System Access
 * handle. (Historically it also had to survive tab switches; since
 * cue-forge-drt.14 RouteView keeps both modes mounted, but the provider still
 * owns handle persistence and lets non-Studio chrome read the video state.)
 * Kept component-free so the provider file satisfies react-refresh.
 */
export interface StudioVideoValue {
  /** The currently loaded video, or null. */
  videoFile: File | null;
  /** Set/replace the video from an <input>-picked File (persists the name only). */
  setVideoFile: (file: File | null) => void;
  /** Filename of a video persisted by a prior session, offered for restore (else null). */
  restorableName: string | null;
  /** True when the browser can re-open a file from a persisted handle. */
  supportsHandles: boolean;
  /** Open a video via the File System Access picker (persists a re-openable handle). */
  loadViaPicker: () => Promise<void>;
  /** Re-open the persisted video. 'ok' = restored; 'repick' = caller should prompt a fresh pick. */
  restoreVideo: () => Promise<'ok' | 'repick'>;
}

export const StudioVideoContext = createContext<StudioVideoValue | null>(null);

/**
 * Access shared Studio video state. Returns null outside a provider — the Editor
 * falls back to local component state then (mirrors the optional QC/Nav contexts).
 */
export function useStudioVideo(): StudioVideoValue | null {
  return useContext(StudioVideoContext);
}
